import {
    FLEX_CENTER_DIV,
    FLEX_COLUMN_DIV,
    FLEX_DIV,
    VERTICAL_SPACER,
} from '@/styles/common.styled.component';
import styled, { css } from 'vue-styled-components';
import CommonButton from '@design/components/button/Button';
import { GRAY } from '@/styles/common.styled';
import Dropdown from '@/components/Common/Dropdown';

import AccordionOpen from '@svgIcons/accordion_open.svg';
import MoveIcon from '@svgIcons/move_column.svg';
import { changeOrder, isValueExist } from '@/utils/utils';
import Draggable from 'vuedraggable';
import { ExportDataService } from '@/services';
import { TYPE_MAP } from '.';
import memoizee from 'memoizee';
import tableMixin from './tableMixin';

const refreshBtnCss = css`
    height: var(--Space-32, 32px);
    min-width: var(--Space-32, 32px);
    border-radius: var(--Radius-100, 8px);
    border: 1px solid ${GRAY(30)};
    background: white;
    font-weight: 400;
    padding: 6px 8px;
    font-size: 14px;
    font-style: normal;
`;

const DROPDOWN_MENU_STYLE = {
    menuItem: css`
        height: 36px;
    `,
};

const COLUMN_TYPES = [
    'bigint',
    'binary',
    'boolean',
    'char',
    'date',
    'decimal',
    'double',
    'float',
    'int',
    'interval',
    'long',
    'smallint',
    'string',
    'timestamp',
    'tinyint',
    'varchar',
];

const refineMetricToSchema = (metric) => {
    return {
        source_column: metric.name,
        target_column: metric.name,
        source_type: metric.type,
        target_type: metric.type,
    };
};

export default {
    name: 'ConfigureColumns',
    mixins: [tableMixin],
    props: {
        metricList: {
            type: Array,
            default: () => [],
        },
        schemas: {
            type: Array,
            default: () => [],
        },
        isEdit: {
            type: Boolean,
            default: false,
        },
        breakdowns: {
            type: Array,
            default: () => [],
        },
        streamType: {
            type: String,
            default: '',
        },
        isAnalytic: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            drag: false,
            originSchemas: [],
        };
    },
    watch: {
        selectedMetrics: {
            handler(metrics) {
                this.generateSchemas(metrics);
            },
            deep: true,
        },
    },
    computed: {
        selectedMetrics: R.compose(
            R.filter(R.prop('selected')),
            R.propOr([], 'metricList')
        ),
        schemaMap: R.compose(
            R.indexBy(R.prop('source_column')),
            R.propOr([], 'schemas')
        ),
        typeInjectedMetrics() {
            return R.compose(
                R.map((metric) => ({
                    ...metric,
                    type: this.getBiCellType(metric.name),
                })),
                R.propOr([], 'selectedMetrics')
            )(this);
        },
    },
    methods: {
        validateColumnName(value) {
            if (!value) return true;
            const regex = /^[a-z][a-z0-9_]*$/;
            return regex.test(value);
        },
        makeSetSchemaItem({ source_column }) {
            const cList = [...this.schemas];
            const targetIndex = R.findIndex(
                R.propEq('source_column', source_column),
                cList
            );

            return ({ key, value }) => {
                const newList = R.set(
                    R.lensPath([targetIndex, key]),
                    value,
                    cList
                );

                this.$emit('setSchemas', newList);
            };
        },
        handleInput(event, columnData, setSchemaValue) {
            const value = event.target.value;
            const prevValue = columnData.target_column;

            if (!this.validateColumnName(value)) {
                // 유효하지 않은 입력인 경우 이전 값으로 되돌림
                event.target.value = prevValue;
                return;
            }

            setSchemaValue({
                key: 'target_column',
                value,
            });
        },
        renderRow(columnData = {}) {
            const setSchemaValue = this.makeSetSchemaItem(columnData);
            return (
                <Body__row>
                    <div class="adriel-flex-vertical-center source-column-name">
                        {/* <div class="adriel-flex-center move-icon">
                            <MoveIcon />
                        </div> */}
                        <div class="adriel-flex-vertical-center column-name">
                            {this.wrapTooltip({
                                visible: true,
                                title: columnData.source_column,
                                content: (
                                    <span class="adriel-ellipsis">
                                        {this.refineMetric({
                                            name: columnData.source_column,
                                        })}
                                    </span>
                                ),
                            })}
                        </div>
                    </div>
                    <div class="adriel-flex-vertical-center target-column-name">
                        <input
                            value={columnData.target_column}
                            onInput={
                                (e) =>
                                    setSchemaValue({
                                        key: 'target_column',
                                        value: e.target.value,
                                    })

                                // this.handleInput(e, columnData, setSchemaValue)
                            }
                            // pattern="^[a-z][a-z0-9_]*$"
                            // title="Must start with a lowercase letter and can only contain lowercase letters, numbers, and underscores"
                        />
                    </div>
                    <div class="adriel-flex-vertical-center column-data-type">
                        <Dropdown
                            options={COLUMN_TYPES}
                            formatter={R.identity}
                            value={
                                columnData.target_type || columnData.source_type
                            }
                            customStyles={DROPDOWN_MENU_STYLE}
                            onInput={(v) => {
                                setSchemaValue({
                                    key: 'target_type',
                                    value: v.value,
                                });
                            }}
                            customDesIcon={
                                <AccordionOpen
                                    v-view-box
                                    width="20px"
                                    height="20px"
                                />
                            }
                        />
                    </div>
                </Body__row>
            );
        },
        getBiCellType: memoizee(function (name) {
            return TYPE_MAP[this.getCellType(name)] || this.getCellType(name);
        }),

        getColumnTypes: memoizee(
            function (typeInjectedMetrics) {
                return ExportDataService.getColumnTypes({
                    userSelectedColumnList: typeInjectedMetrics,
                    connectorType: this.isAnalytic ? 'GENERIC' : 'MARKETING',
                    streamType: this.streamType,
                    breakdowns: R.compose(
                        R.pluck('id'),
                        R.filter(R.prop('selected'))
                    )(this.breakdowns),
                });
            },
            {
                normalizer: R.compose(
                    JSON.stringify,
                    R.sort(R.comparator(R.gt)),
                    R.pluck('name'),
                    R.head
                ),
                promise: true,
            }
        ),
        onClickReset() {
            // if (this.isEdit && isValueExist(this.schemas)) {
            //     this.$emit('setSchemas', this.originSchemas);
            //     return;
            // }

            return this.getColumnTypes(this.typeInjectedMetrics).then(
                (columnTypes) => {
                    const refinedSchemas =
                        columnTypes.map(refineMetricToSchema);

                    this.$emit('setSchemas', refinedSchemas);
                }
            );
        },

        generateSchemas(metrics) {
            if (!isValueExist(metrics)) {
                return [];
            }

            return this.getColumnTypes(this.typeInjectedMetrics).then(
                (columnTypes) => {
                    const refinedSchemas = columnTypes.map((metric) => {
                        return {
                            ...(this.schemaMap[metric.name] ||
                                refineMetricToSchema(metric)),
                        };
                    });

                    this.$emit('setSchemas', refinedSchemas);
                    return refinedSchemas;
                }
            );
        },
    },
    mounted() {
        if (isValueExist(this.selectedMetrics)) {
            this.generateSchemas(this.selectedMetrics).then(
                (refinedSchemas) => {
                    if (this.isEdit) {
                        this.originSchemas = refinedSchemas;
                    }
                }
            );
        }
    },
    render() {
        // TODO
        const dataModel = this.schemas;
        const model = [...dataModel];

        return (
            <Wrapper class="configure-columns">
                <Headline>
                    <Headline__title>
                        {this.$t(
                            'EXPORT_DATA.panel_description_configure_columns'
                        )}
                    </Headline__title>
                    <CommonButton
                        type="white"
                        css={refreshBtnCss}
                        onClick={this.onClickReset}
                        class="reset-btn"
                    >
                        {this.$t('COMMON.reset')}
                    </CommonButton>
                </Headline>

                <VERTICAL_SPACER amount={20} />

                <Body>
                    <Body__head>
                        <div>
                            {this.$t(
                                'EXPORT_DATA.configure_column_source_column_name'
                            )}
                        </div>
                        <div>
                            {this.$t(
                                'EXPORT_DATA.configure_column_target_column_name'
                            )}
                        </div>
                        <div>
                            {this.$t('EXPORT_DATA.configure_column_data_type')}
                        </div>
                    </Body__head>
                    <Body__rows class={{ 'no-select': this.drag }}>
                        <Draggable
                            value={model}
                            animation={200}
                            group="row"
                            ghostClass="ghost"
                            class="drag-area"
                            forceFallback={true}
                            handle=".move-icon"
                            on={{
                                start: () => (this.drag = true),
                                end: ({ oldIndex, newIndex }) => {
                                    const list = this.schemas;
                                    const _f = R.findIndex(R.__, list);
                                    oldIndex = _f(
                                        R.equals(dataModel[oldIndex])
                                    );
                                    newIndex = _f(
                                        R.equals(dataModel[newIndex])
                                    );
                                    const newModel = changeOrder({
                                        oldIndex,
                                        newIndex,
                                        value: list,
                                    });
                                    this.drag = false;

                                    this.$emit('setSchemas', newModel);
                                },
                            }}
                        >
                            {this.schemas.map(this.renderRow)}
                        </Draggable>
                    </Body__rows>
                </Body>
            </Wrapper>
        );
    },
};

const Wrapper = styled(FLEX_COLUMN_DIV)``;

const Headline = styled(FLEX_DIV)`
    .reset-btn {
        margin-left: auto;
    }
`;

const Headline__title = styled(FLEX_CENTER_DIV)`
    color: var(--gray-gray-802-a-333-d, #2a333d);
    font-feature-settings: 'liga' off, 'clig' off;
    font-family: var(--font-family-primary);
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
`;

const Body = styled(FLEX_COLUMN_DIV)``;

const Body__head = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    align-self: stretch;
    margin-bottom: 12px;

    & > div {
        color: ${GRAY(90)};
        font-feature-settings: 'liga' off, 'clig' off;
        font-family: var(--font-family-primary);
        font-size: 13px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        flex: 1 0 0;
    }
`;

const Body__rows = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--Space-6, 6px);
    align-self: stretch;
    color: ${GRAY(100)};

    .drag-area {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: var(--Space-6, 6px);
        align-self: stretch;
    }

    &.no-select {
        user-select: none;
        -moz-user-select: none;
        -webkit-user-select: none;
        -ms-user-select: none;
    }
`;

const Body__row = styled.div`
    display: flex;
    padding: 12px;
    align-items: center;
    gap: 8px;
    align-self: stretch;
    border-radius: var(--Radius-100, 8px);
    background: ${GRAY(5)};
    color: var(--gray-gray-100080-a-0-d, #080a0d);
    font-family: var(--font-family-primary);
    font-size: 13px;
    font-style: normal;
    font-weight: 400;

    & > {
        height: var(--Space-32, 32px);
    }

    .move-icon {
        width: var(--Space-32, 32px);
        padding: 4px;
        cursor: move;
    }

    .source-column-name {
        padding: var(--Space-8, 8px) var(--Space-0, 0px);
        gap: var(--Space-8, 8px);
        flex: 1 0 0;
        color: var(--gray-gray-100080-a-0-d, #080a0d);
        line-height: normal;

        .column-name {
            max-width: 200px;
        }
    }

    .target-column-name {
        gap: var(--Space-8, 8px);
        flex: 1 0 0;
        width: 203px;

        input {
            padding: var(--Space-8, 8px) var(--Space-12, 12px);
            border-radius: 2px;
            border: 1px solid var(--gray-gray-25-d-4-d-8-db, #d4d8db);
            background: var(--white-white, #fff);
            height: 100%;
            line-height: normal;
        }
    }

    .column-data-type {
        height: 32px;
        gap: var(--Space-8, 8px);
        flex: 1 0 0;

        .dropdown-trigger {
            width: 203px;
            height: 100%;
            line-height: normal;
            border-radius: 2px;
            border: 1px solid var(--gray-gray-25-d-4-d-8-db, #d4d8db);
            background: var(--white-white, #fff);
        }
    }
`;
